import { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Eye, EyeOff, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { resetPassword } from '@/lib/api';
import { RTBLogo } from '@/components/RTBLogo';

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    if (password !== confirm) { toast.error('Passwords do not match'); return; }
    setLoading(true);
    try {
      await resetPassword(token, password);
      setSuccess(true);
      toast.success('Password reset! You can now sign in.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-rtb-mint/30 flex items-center justify-center px-4">
        <div className="text-center">
          <p className="text-gray-500 mb-4">Invalid reset link.</p>
          <Link to="/login" className="text-rtb-purple font-semibold hover:underline">Go to Login</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-rtb-mint/30 flex items-center justify-center px-4" data-testid="reset-password-page">
      <div className="fixed top-20 right-20 w-72 h-72 bg-rtb-purple/5 rounded-full blur-[100px]" />
      <div className="fixed bottom-20 left-20 w-72 h-72 bg-rtb-green/5 rounded-full blur-[100px]" />

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="max-w-md w-full relative z-10">
        <div className="text-center mb-8">
          <Link to="/" className="inline-block mb-6">
            <RTBLogo className="h-10 mx-auto" />
          </Link>
          <h1 className="text-3xl font-oswald font-bold uppercase text-gray-900">
            Reset <span className="text-rtb-purple">Password</span>
          </h1>
          <p className="text-gray-500 text-sm mt-2">Enter your new password</p>
        </div>

        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="glass-light p-8 space-y-5 rounded-2xl">
          {success ? (
            <div className="text-center py-4">
              <CheckCircle className="w-12 h-12 text-rtb-green mx-auto mb-4" />
              <h3 className="font-oswald font-bold text-lg text-gray-900 mb-2">Password Reset!</h3>
              <p className="text-gray-500 text-sm mb-6">Your password has been updated successfully.</p>
              <Button onClick={() => navigate('/login')} className="btn-primary w-full" data-testid="go-to-login-btn">
                Sign In <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="relative">
                <Label className="text-gray-600 mb-1.5 block text-sm font-medium">New Password</Label>
                <Input type={showPassword ? "text" : "password"} value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-styled pr-12" required placeholder="At least 6 characters"
                  data-testid="new-password" />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-[38px] text-gray-400 hover:text-gray-600">
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <div>
                <Label className="text-gray-600 mb-1.5 block text-sm font-medium">Confirm Password</Label>
                <Input type="password" value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="input-styled" required placeholder="Confirm your password"
                  data-testid="confirm-password" />
              </div>
              <Button type="submit" disabled={loading} className="btn-primary w-full" data-testid="reset-submit-btn">
                {loading ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> :
                  <>Reset Password <ArrowRight className="ml-2 w-5 h-5" /></>}
              </Button>
              <p className="text-center text-gray-400 text-sm">
                <Link to="/login" className="text-rtb-purple font-semibold hover:underline">Back to Sign In</Link>
              </p>
            </form>
          )}
        </motion.div>
      </motion.div>
    </div>
  );
}
